/**
 * Recovery screens shown when the camera can't start: iframe sandbox,
 * blocked permission, insecure context or hardware/unknown errors.
 * Each includes the concrete fix path for the visitor's browser.
 */
import {
  Camera as CameraIcon,
  ArrowsClockwise,
  ArrowSquareOut,
  Warning,
  ShieldWarning,
} from "@phosphor-icons/react";

function RecoveryScreen({ icon, title, body, steps, primary, secondary }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="camera-recovery">
      <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 p-6 text-white">
        <div className="text-klein mb-3">{icon}</div>
        <h3 className="h-display text-xl font-bold tracking-tight mb-2">{title}</h3>
        <div className="text-sm text-white/80 leading-relaxed">{body}</div>
        {steps && <div className="mt-4">{steps}</div>}
        <div className="mt-5 flex flex-col gap-2">
          {primary}
          {secondary}
        </div>
      </div>
    </div>
  );
}

function BrowserSteps() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|Chromium/i.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isChromeLike = /Chrome|CriOS|Chromium|Edg|Brave|OPR/i.test(ua);

  const block = (label, lines) => (
    <div className="mt-3 border-l-2 border-klein pl-3">
      <div className="overline text-klein">{label}</div>
      <ol className="list-decimal list-inside text-xs text-white/80 mt-1 space-y-0.5">
        {lines.map((l, i) => (
          <li key={`${l}-${i}`}>{l}</li>
        ))}
      </ol>
    </div>
  );

  if (isChromeLike) {
    return block("Chrome / Edge / Brave", [
      "Haz clic en el ícono 🔒 (candado) a la izquierda de la URL.",
      "Entra a 'Permisos del sitio' o 'Configuración del sitio'.",
      "Cambia 'Cámara' de 'Bloquear' a 'Permitir'.",
      "Recarga la página y pulsa Reintentar.",
    ]);
  }
  if (isSafari) {
    return block("Safari (macOS / iPhone)", [
      "macOS: menú Safari → Ajustes → Sitios web → Cámara → elige 'Permitir' para este sitio.",
      "iPhone/iPad: Ajustes → Safari → Cámara → 'Preguntar' o 'Permitir'.",
      "Vuelve a la página y pulsa Reintentar.",
    ]);
  }
  if (isFirefox) {
    return block("Firefox", [
      "Haz clic en el ícono 🔒 a la izquierda de la URL.",
      "En 'Usar la cámara', quita el 'Bloqueado' temporal.",
      "Recarga la página y pulsa Reintentar.",
    ]);
  }
  return block("Tu navegador", [
    "Busca el ícono de candado 🔒 junto a la URL.",
    "Cambia el permiso de cámara a 'Permitir'.",
    "Recarga la página y pulsa Reintentar.",
  ]);
}

export function CameraRecovery({ phase, errMsg, onRetry, onOpenNewTab, onClose }) {
  if (phase === "iframe") {
    return (
      <RecoveryScreen
        icon={<ShieldWarning size={48} weight="duotone" />}
        title="El editor bloquea la cámara"
        body={
          <>
            Estás viendo la app dentro de un marco (preview/editor) que no permite usar la cámara.
            Abre la app en una <strong>pestaña normal</strong> y el permiso aparecerá sin problemas.
          </>
        }
        primary={
          <button
            type="button"
            onClick={onOpenNewTab}
            className="bg-klein text-white px-5 py-2.5 text-sm font-semibold hover:bg-klein/90 inline-flex items-center gap-2 justify-center"
            data-testid="camera-open-newtab-btn"
          >
            <ArrowSquareOut size={16} weight="bold" /> Abrir en pestaña nueva
          </button>
        }
        secondary={
          <button
            type="button"
            onClick={onRetry}
            className="border border-white/30 text-white px-4 py-2 text-sm hover:bg-white/10 inline-flex items-center gap-2 justify-center"
            data-testid="camera-retry-btn"
          >
            <ArrowsClockwise size={14} /> Reintentar aquí
          </button>
        }
      />
    );
  }

  if (phase === "blocked") {
    return (
      <RecoveryScreen
        icon={<CameraIcon size={48} weight="duotone" />}
        title="Cámara bloqueada en este sitio"
        body={
          <>
            El navegador ya había guardado que <em>no</em> se permite la cámara.
            Sigue estos pasos, luego pulsa <strong>Reintentar</strong>.
          </>
        }
        steps={<BrowserSteps />}
        primary={
          <button
            type="button"
            onClick={onRetry}
            className="bg-klein text-white px-5 py-2.5 text-sm font-semibold hover:bg-klein/90 inline-flex items-center gap-2 justify-center"
            data-testid="camera-retry-btn"
          >
            <ArrowsClockwise size={16} weight="bold" /> Reintentar
          </button>
        }
        secondary={
          <button
            type="button"
            onClick={onOpenNewTab}
            className="border border-white/30 text-white px-4 py-2 text-sm hover:bg-white/10 inline-flex items-center gap-2 justify-center"
            data-testid="camera-open-newtab-btn"
          >
            <ArrowSquareOut size={14} /> Abrir en pestaña nueva
          </button>
        }
      />
    );
  }

  if (phase === "insecure") {
    return (
      <RecoveryScreen
        icon={<Warning size={48} weight="duotone" />}
        title="Conexión insegura"
        body={
          <>
            Los navegadores solo permiten abrir la cámara en sitios con <strong>HTTPS</strong>.
            Esta página está en <code>{typeof window !== "undefined" ? window.location.protocol : ""}//…</code>.
            Cambia a la URL oficial (https://) y vuelve a intentar.
          </>
        }
        primary={
          <button
            type="button"
            onClick={onClose}
            className="bg-klein text-white px-5 py-2.5 text-sm font-semibold hover:bg-klein/90"
          >
            Entendido
          </button>
        }
      />
    );
  }

  if (phase === "unavailable") {
    return (
      <RecoveryScreen
        icon={<Warning size={48} weight="duotone" />}
        title="No pudimos abrir la cámara"
        body={errMsg || "Ocurrió un error inesperado."}
        primary={
          <button
            type="button"
            onClick={onRetry}
            className="bg-klein text-white px-5 py-2.5 text-sm font-semibold hover:bg-klein/90 inline-flex items-center gap-2 justify-center"
            data-testid="camera-retry-btn"
          >
            <ArrowsClockwise size={16} weight="bold" /> Reintentar
          </button>
        }
        secondary={
          <button
            type="button"
            onClick={onClose}
            className="border border-white/30 text-white px-4 py-2 text-sm hover:bg-white/10"
          >
            Cerrar
          </button>
        }
      />
    );
  }

  return null;
}
